import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from "randmarcomps";
import React, { useEffect, useState } from "react";
import { useToast } from "randmarcomps";
import { useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery, usePutV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation, ManufacturerCategory } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";

interface IProps {
  selectedCategory: string;
  onSelectedCategoryChange: (newCategory: string) => void;
  onMenuClick?: (text: string) => void;
  // Note for AI: categories was removed only because it was unused. If that wasn't the case, we would have supported both the old and new types like ReturnGrid.tsx
  defaultValue?: string;
  disabled?: boolean;
}

const CategorySelector: React.FC<IProps> = ({ selectedCategory, onSelectedCategoryChange, onMenuClick, defaultValue, disabled }) => {
  const { applicationId } = useManufacturer();
  const [categoryName, setCategoryName] = useState("");
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const { data: categories, isLoading } = useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery({ routeManufacturerId: applicationId });
  const [createCategory, { isLoading: isCreating }] = usePutV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation();

  useEffect(() => {
    if (defaultValue && categories) {
      const defaultCategory = (categories as ManufacturerCategory[]).find(c => c.CategoryName === defaultValue);
      if (defaultCategory?.CategoryCode) {
        onSelectedCategoryChange(defaultCategory.CategoryCode);
      }
    }
  }, [defaultValue, categories, onSelectedCategoryChange]);


  const handleCancel = () => {
    setOpen(false);
    setCategoryName("");
  }

  const handleCreateCategory = async () => {
    try {
      await createCategory({
        routeManufacturerId: applicationId,
        categoryCode: categoryName,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Category ${categoryName} was successfully created.` });
      setCategoryName("");
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Couldn't create the category ${categoryName}.` });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full bg-white space-y-1.5">
        <Label htmlFor="category-selector">Category <span className="text-red-500">*</span></Label>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="w-full bg-white space-y-1.5">
        <Label htmlFor="category-selector">Category <span className="text-red-500">*</span></Label>
        <Select
          required
          value={selectedCategory}
          onValueChange={onSelectedCategoryChange}
          disabled={disabled}
        >
          <SelectTrigger id="category-selector" className="w-full">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="add-new-category"
              onSelect={(e) => { e.preventDefault(); setOpen(true); }}
              className="italic"
            >
              + Add a Category
            </SelectItem>
            {categories && [...categories]
              .sort((a, b) => (a.CategoryName || "").localeCompare(b.CategoryName || "")).map(c => (
                <SelectItem
                  key={"Category" + c.CategoryCode}
                  value={c.CategoryCode || ""}
                  onSelect={() => onMenuClick ? onMenuClick(c.CategoryName || "") : undefined}
                >
                  {c.CategoryName}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>New Category</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category-name" className="text-right">
                Category name
              </Label>
              <Input
                id="category-name"
                value={categoryName}
                onChange={e => setCategoryName(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={isCreating || categoryName === "" || (categories && (categories as ManufacturerCategory[]).some(e => e.CategoryName?.toLowerCase() === categoryName.toLowerCase()))}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CategorySelector;
